import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Link, useNavigate, useLocation } from "react-router-dom";
import AuthHeader from "./AuthHeader";

// Create a dedicated component for success message (same as in Login.js)
const SuccessMessage = ({ message }) => (
  <div
    style={{
      backgroundColor: "#e8f5e9",
      color: "#2e7d32",
      padding: "12px 16px",
      borderRadius: "4px",
      marginBottom: "20px",
      fontSize: "14px",
      fontWeight: "500",
      textAlign: "center",
      border: "2px solid #4caf50", // Making the border more obvious
      boxShadow: "0 0 0 1px #e8f5e9", // Adding a shadow for more emphasis
    }}
  >
    {message}
  </div>
);

const Register = () => {
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState(""); // New state for success messages
  const [isLoading, setIsLoading] = useState(false);
  const [organization, setOrganization] = useState("");

  const { signUp } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Check for any success messages passed from other components
  useEffect(() => {
    if (location.state?.successMessage) {
      setSuccessMessage(location.state.successMessage);
    }
  }, [location]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Clear previous messages
    setError("");
    setSuccessMessage("");

    // Validation
    if (!username || !email || !password || !confirmPassword) {
      setError("Please fill in all fields");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }

    try {
      setIsLoading(true);
      await signUp(email, password, username, organization);

      // Use a very specific success message for consistent detection
      const successMsg =
        "Registration successful! Please verify your email and log in.";
      console.log("Register: Setting local success message:", successMsg);

      // Set success message locally
      setSuccessMessage(successMsg);

      // Debug what we're about to pass to navigation
      console.log("Register: About to navigate with state:", {
        message: successMsg,
      });

      // Explicitly serialize and deserialize to check for any transformation
      const serialized = JSON.stringify({ message: successMsg });
      console.log("Register: Serialized state:", serialized);
      const deserialized = JSON.parse(serialized);
      console.log("Register: Deserialized state:", deserialized);

      // Navigate after a short delay with the SAME message
      setTimeout(() => {
        console.log("Register: Navigating to login with message:", successMsg);
        navigate("/login", {
          state: { message: successMsg },
        });
      }, 1500);
    } catch (err) {
      setError(err.message || "Failed to create account");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="register-container" style={styles.container}>
      <div style={styles.formWrapper}>
        <AuthHeader /> {/* Add the header component here */}
        <h2 style={styles.title}>Join today!</h2>
        {/* Show either error or success message, but not both */}
        {error && (
          <div
            style={{
              backgroundColor: "#ffebee",
              color: "#c62828",
              padding: "12px 16px",
              borderRadius: "4px",
              marginBottom: "20px",
              fontSize: "14px",
              textAlign: "center",
              border: "2px solid #f44336",
            }}
          >
            {error}
          </div>
        )}
        {successMessage && <SuccessMessage message={successMessage} />}
        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.inputGroup}>
            <label htmlFor="username" style={styles.label}>
              Username
            </label>
            <input
              type="text"
              id="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              style={styles.input}
              required
            />
          </div>

          <div style={styles.inputGroup}>
            <label htmlFor="email" style={styles.label}>
              Email
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={styles.input}
              required
            />
          </div>

          <div style={styles.inputGroup}>
            <label htmlFor="password" style={styles.label}>
              Password
            </label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={styles.input}
              required
            />
            <p style={styles.hint}>Password must be at least 6 characters</p>
          </div>

          <div style={styles.inputGroup}>
            <label htmlFor="confirmPassword" style={styles.label}>
              Confirm Password
            </label>
            <input
              type="password"
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              style={styles.input}
              required
            />
          </div>

          <div style={styles.inputGroup}>
            <label
              htmlFor="organization"
              style={{ ...styles.label, fontStyle: "italic" }}
            >
              Which tutoring service are you with?
            </label>
            <select
              id="organization"
              value={organization}
              onChange={(e) => setOrganization(e.target.value)}
              style={styles.input}
            >
              <option value="na">N/A</option>
              <option value="mathnasium">Mathnasium</option>
              <option value="kumon">Kumon</option>
              <option value="learnika">Learnika</option>
              <option value="opmath">OpMath</option>
              <option value="chandler">Chandler Learning Center</option>
              <option value="teachthemtothrive">Teach Them To Thrive</option>
              <option value="c2">C2 Education</option>
              <option value="paloaltomathtutoring">
                Palo Alto Math Tutoring
              </option>
              <option value="axiom">Axiom Learning</option>
            </select>
          </div>

          <button type="submit" disabled={isLoading} style={styles.button}>
            {isLoading ? "Creating account..." : "Register"}
          </button>
        </form>
        <p style={styles.loginText}>
          Already have an account?{" "}
          <Link to="/login" style={styles.link}>
            Log in
          </Link>
        </p>
      </div>
    </div>
  );
};

const styles = {
  container: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "100vh",
    backgroundColor: "#fff9e9",
    fontFamily: "'Inter', sans-serif",
  },
  formWrapper: {
    backgroundColor: "white",
    padding: "30px",
    borderRadius: "15px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
    width: "100%",
    maxWidth: "400px",
  },
  title: {
    textAlign: "center",
    color: "#333",
    marginBottom: "24px",
    fontWeight: "700",
    fontSize: "28px",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "20px",
  },
  inputGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  label: {
    fontWeight: "600",
    color: "#333",
    fontSize: "16px",
  },
  input: {
    padding: "12px 16px",
    borderRadius: "8px",
    border: "1px solid #ddd",
    fontSize: "16px",
    backgroundColor: "#fbfbfb",
    transition: "all 0.3s ease",
    outline: "none",
  },
  hint: {
    fontSize: "12px",
    color: "#888",
    margin: "4px 0 0",
  },
  button: {
    padding: "14px 20px",
    backgroundColor: "#9CDAED",
    color: "#000",
    fontWeight: "600",
    fontSize: "18px",
    borderRadius: "8px",
    border: "none",
    cursor: "pointer",
    transition: "all 0.3s ease",
    outline: "none",
    marginTop: "10px",
  },
  error: {
    backgroundColor: "#ffebee",
    color: "#c62828",
    padding: "12px 16px",
    borderRadius: "4px",
    marginBottom: "20px",
    fontSize: "14px",
    textAlign: "center",
  },
  success: {
    backgroundColor: "#e8f5e9", // Ensure this is a green color
    color: "#2e7d32", // Ensure this is a green color
    padding: "12px 16px",
    borderRadius: "4px",
    marginBottom: "20px",
    fontSize: "14px",
    fontWeight: "500", // Slightly bolder for success
    textAlign: "center",
    border: "1px solid #a5d6a7", // Add border for more distinct styling
  },
  loginText: {
    marginTop: "24px",
    textAlign: "center",
    color: "#666",
    fontSize: "14px",
  },
  link: {
    color: "#9CDAED",
    textDecoration: "none",
    fontWeight: "600",
  },
};

export default Register;
